<script setup>
import { computed, inject } from 'vue';
import { textItems } from '../blockContent.js';
import { styleVars } from './shared.js';
import { safeEmbedHtml, safeRichHtml } from '../safeHtml.js';
import { isRich } from '../richText';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// The items array is the shape the editor writes; the legacy single
// mode/text/html/level fields still render, so an old block needs no
// migration to switch template.
const allItems = computed(() => {
    if (Array.isArray(props.config.items) && props.config.items.length) return props.config.items;
    return [{
        type: props.config.mode || 'paragraph',
        level: props.config.level || 'h2',
        text: props.config.text || '',
        html: props.config.html || '',
        html_safe: props.config.html_safe,
    }];
});

// A visitor sees only the items with words in them, and a block with
// none draws nothing: no empty band, no "Your heading" starter copy.
// The editor keeps them all (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const items = computed(() => textItems(allItems.value, editing));

const align = computed(() => (['left', 'center', 'right'].includes(props.config.align) ? props.config.align : 'left'));
const widthClass = computed(() => `th-text__inner--${props.config.width || 'medium'}`);

// SANITIZED, not trusted — the same rules as Classic's TextBlock, in
// one place (../safeHtml.js), which also renders on the server.

function tagFor(item) {
    if (item.type !== 'heading') return null;
    return ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(item.level) ? item.level : 'h2';
}
</script>

<template>
    <section v-if="items.length" :id="config.anchor_id || null" class="th-block th-text" :style="styleVars(config)">
        <div class="th-wrap th-sec">
            <div class="th-prose th-text__inner" :class="[widthClass, `th-text__inner--align-${align}`]">
                <template v-for="(item, i) in items" :key="i">
                    <component v-if="item.type === 'heading'" :is="tagFor(item)"
                               class="th-text__heading" :class="`th-text__heading--${tagFor(item)}`">
                        {{ item.text }}
                    </component>
                    <!-- A paragraph written with the toolbar (bold, links,
                         lists) is stored as html beside a plain reading;
                         printing the plain one dropped every link on
                         Trailhead and Backyard. Sanitized: ../safeHtml.js. -->
                    <div v-else-if="item.type === 'paragraph' && isRich(item)" class="th-text__paragraph th-text__rich" v-html="safeRichHtml(item)"></div>
                    <p v-else-if="item.type === 'paragraph'" class="th-text__paragraph">{{ item.text }}</p>
                    <!-- Sanitized: see ../safeHtml.js. -->
                    <div v-else-if="item.type === 'html'" class="th-text__html" v-html="safeEmbedHtml(item)"></div>
                </template>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* th-prose pins its own reading colour; the compound selector is what
   lets an editor Style override still reach it. */
.th-text .th-text__inner { margin: 0 auto; color: var(--block-text, #2a2d29); }
.th-text__inner--narrow { max-width: 600px; }
.th-text__inner--medium { max-width: 760px; }
.th-text__inner--wide { max-width: 1000px; }
.th-text__inner--full { max-width: none; }
.th-text__inner--align-left { text-align: left; }
.th-text__inner--align-center { text-align: center; }
.th-text__inner--align-right { text-align: right; }

/* th-prose carries the body rhythm; headings take the display face at
   the sizes the rest of the template uses. */
.th-text__heading {
    font-family: var(--th-display);
    font-weight: 800;
    line-height: 1.04;
    letter-spacing: -.015em;
    text-wrap: balance;
    color: var(--text-heading, inherit);
}
.th-text__heading--h1 { font-size: clamp(40px, 6vw, 72px); font-stretch: 110%; }
.th-text__heading--h2 { font-size: clamp(30px, 4vw, 48px); font-stretch: 108%; }
.th-text__heading--h3 { font-size: clamp(24px, 3vw, 32px); font-stretch: 106%; }
.th-text__heading--h4 { font-size: 22px; }
.th-text__heading--h5 { font-size: 18px; }
.th-text__heading--h6 {
    font: 500 12px/1.2 var(--th-mono);
    letter-spacing: .1em;
    text-transform: uppercase;
    color: var(--text-eyebrow, var(--accent));
}
.th-text__paragraph { white-space: pre-wrap; }
.th-text__rich { white-space: normal; }
.th-text__rich :deep(p) { margin: 0 0 0.9em; }
.th-text__rich :deep(> :last-child) { margin-bottom: 0; }
/* The block's own text color, underlined: an accent picked for the
   theme can vanish on a background picked for this block. */
.th-text__rich :deep(a) { color: inherit; text-decoration: underline; text-underline-offset: 2px; font-weight: 600; }
.th-text__rich :deep(a:hover) { text-decoration-thickness: 2px; }
.th-text__rich :deep(ul), .th-text__rich :deep(ol) { margin: 0 0 0.9em; padding-left: 1.4em; text-align: left; }
.th-text__rich :deep(ul) { list-style: disc; }
.th-text__rich :deep(ol) { list-style: decimal; }
.th-text__rich :deep(li) { margin: 0.2em 0; }
.th-text__rich :deep(li p) { margin: 0; }
.th-text__rich :deep(blockquote) { margin: 0 0 0.9em; padding-left: 16px; border-left: 3px solid currentColor; opacity: 0.85; font-style: italic; }

/* A heading and the paragraph under it read as one unit. */
.th-text__heading + .th-text__paragraph { margin-top: -10px; }

.th-text__html :deep(iframe),
.th-text__html :deep(video) { max-width: 100%; width: 100%; border: 0; display: block; }
.th-text__html :deep(iframe:not([height])) { aspect-ratio: 16 / 9; height: auto; }
.th-text__html :deep(img) { max-width: 100%; height: auto; border-radius: var(--r); }
</style>
